// /patient/pre-visit — full-screen pre-visit welcome block. Renders the
// "Before your visit" content from the mockup as its own page (linked from
// the home hero CTA), with Start (deep-link to /visit/:id) / Skip controls.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  doctorLabel,
  firstNameOf,
  formatLongDate,
  formatTime,
  usePatientData,
  useUpcomingAndPast,
  untilLabel,
} from "./PatientDataContext";
import { getPatientPageVisitPathSegment } from "../../utils/visitPathUtils";

export default function PatientPreVisit() {
  const navigate = useNavigate();
  const { userRow, rows } = usePatientData();
  const { upcoming } = useUpcomingAndPast(rows);
  const nextVisit = upcoming[0] || null;
  const doctor = doctorLabel(nextVisit || rows[0] || {});

  if (!nextVisit) {
    return (
      <div className="pp-card pp-tinted-teal">
        <div className="pp-visit-title">No upcoming visit</div>
        <p className="pp-detail-p">
          You don't have a visit on the books right now. When your clinic
          schedules one, the pre-visit questions will live here.
        </p>
        <Link to="appointments" className="pp-btn pp-teal">
          See my appointments
        </Link>
      </div>
    );
  }

  const startChat = () => {
    const seg = getPatientPageVisitPathSegment(nextVisit);
    if (!seg) return;
    navigate(`/visit/${seg}`, {
      state: {
        rowData: nextVisit,
        date_: nextVisit.date_,
        name: nextVisit.patient_name || nextVisit.name || "Unknown",
      },
    });
  };

  return (
    <div className="pp-card pp-previsit">
      <div className="pp-previsit-head">
        <span className="pp-previsit-eyebrow">Before your visit</span>
      </div>
      <div className="pp-previsit-title">
        {doctor} · {formatLongDate(nextVisit.date_)}
        {nextVisit.time_ ? ` · ${formatTime(nextVisit.time_)}` : ""}
      </div>

      <div className="pp-msg-bubble">
        <div className="pp-msg-meta">💬 From VitaMed · text message</div>
        <p>
          Hi {firstNameOf(userRow)}. You have an appointment with {doctor} on{" "}
          {formatLongDate(nextVisit.date_)}
          {nextVisit.time_ ? ` at ${formatTime(nextVisit.time_)}` : ""}. Can you
          tell us why you're coming in — in your own words? It takes about 5
          minutes and your doctor will read it before you walk in.
        </p>
        <div className="pp-msg-link">vitamed.app/v/{nextVisit.id || "aB7-3K"}</div>
      </div>

      <p className="pp-detail-p pp-previsit-lede">
        <strong>Five minutes that save your visit.</strong> A friendly
        conversation about what's going on with you. Your answers go straight
        into your chart — so when your doctor walks in, you don't have to start
        from the beginning.
      </p>

      <div className="pp-previsit-cols">
        <div className="pp-previsit-col">
          <div className="pp-previsit-col-h">📋 Why we ask</div>
          <p className="pp-detail-p" style={{ margin: 0 }}>
            You shouldn't have to tell the form, then the nurse, then the
            doctor — losing pieces each time. Tell us once, well. That's the
            whole idea.
          </p>
        </div>
        <div className="pp-previsit-col">
          <div className="pp-previsit-col-h">🔒 Three promises</div>
          <ul className="pp-previsit-list">
            <li>We <strong>never</strong> use your information to train AI.</li>
            <li>We <strong>never</strong> show you ads.</li>
            <li>We <strong>never</strong> sell your data.</li>
          </ul>
        </div>
      </div>

      <div className="pp-previsit-col">
        <div className="pp-previsit-col-h">What we'll cover</div>
        <ul className="pp-previsit-list pp-previsit-checks">
          <li><span>🤔</span> What's bringing you in</li>
          <li><span>🕒</span> When it started and how it feels</li>
          <li><span>💊</span> Any medication changes</li>
          <li><span>❓</span> Questions you want {doctor} to answer</li>
        </ul>
      </div>

      <label className="pp-previsit-consent">
        <input type="checkbox" defaultChecked />
        <span>I understand my answers become part of my medical chart.</span>
      </label>

      <div className="pp-previsit-actions">
        <button type="button" className="pp-btn pp-teal" onClick={startChat}>
          → Let's start
        </button>
        <Link to="." className="pp-btn pp-ghost">
          Skip for now
        </Link>
      </div>

      <div style={{ marginTop: 14 }}>
        <span className="pp-row-pill">
          {untilLabel(nextVisit.date_)}
        </span>
      </div>
    </div>
  );
}